import moment from 'moment-timezone';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import MiscUtils from 'services/MiscUtils';
import CalendarUtils from 'services/CalendarUtils';
import imageFemale from 'assets/img/icons/avatar-female.svg';
import imageMale from 'assets/img/icons/avatar-male.svg';
import { Appointment, HealthCard, Patient } from '../generated/api';
import { type PatientData } from '../careand-redux/slices/healthRecords';

class PatientUtils {
  /**
   * @param {Patient} patient
   * @param {string | undefined} errorPlaceholder
   * @param {boolean | undefined} includeShortName
   * @param {boolean | undefined} fullName
   * @param {boolean | undefined} invert
   */
  getName(
    patient: Patient | undefined,
    errorPlaceholder?: string,
    includeShortName = true,
    fullName = true,
    invert = false,
  ) {
    let error = errorPlaceholder;

    if (!patient) return error;

    let name = '';

    let { first_name, second_name, last_name } = patient || {};

    if (first_name) name = first_name;
    if (fullName && !!second_name) name += ` ${second_name}`;
    if (last_name) name += ` ${last_name}`;
    if (includeShortName) {
      let elements = [];
      if (patient.preferred_name) {
        elements.push(patient.preferred_name.trim());
      }
      if (patient.preferred_pronoun) {
        elements.push(patient.preferred_pronoun.trim());
      }
      if (elements.length > 0) {
        if (invert) {
          name = `(${elements.join(', ')}) ${name}`;
        } else {
          name += ` (${elements.join(', ')})`;
        }
      }
    }

    name = name.trim();

    if (name === '') name = error as string;
    if (name === error && errorPlaceholder !== undefined) name = errorPlaceholder;

    if (!name) name = 'Unknown Name';

    return name;
  }

  getPreferredName(patient: Patient | undefined) {
    if (patient?.preferred_name) return patient.preferred_name;
    return this.getName(patient);
  }

  getShortName(patient?: Patient, errorPlaceholder?: string) {
    let error = undefined;
    let name = this.getName(patient, undefined, false);

    if (name !== undefined) {
      let split = name.split(' ');

      name = split[0]!;

      if (split.length > 1) name += ` ${split[split.length - 1]!.charAt(0)}.`;

      name = name.trim();

      if (name === '') name = error;
    }

    if (name === error && errorPlaceholder !== undefined) name = errorPlaceholder;

    return name;
  }

  getInitials(patient: Patient | undefined) {
    let { first_name, last_name } = patient || {};
    return (first_name || ' ')[0]! + (last_name || ' ')[0]!;
  }

  getAge(patient?: { date_of_birth?: number; date_deceased?: number }) {
    let age = 'Unknown Age';

    if (patient === undefined) return age;

    const { date_of_birth } = patient;
    let { date_deceased } = patient;

    if (date_of_birth !== undefined && date_of_birth !== 0) {
      age = CalendarUtils.calculateAge(date_of_birth, date_deceased);
    }

    return age;
  }

  getAgeAsNumber(patient: Patient | undefined) {
    let age = undefined;

    if (patient === undefined) return age;

    const { date_of_birth } = patient;
    const { date_deceased } = patient;

    if (date_of_birth !== undefined && date_of_birth !== 0) {
      age = CalendarUtils.calculateAgeNumber(date_of_birth, date_deceased);
    }

    return age;
  }

  getBirthdate(
    patient: { date_of_birth?: number } | undefined,
    format = 'Do MMMM YYYY',
    includeAge = true,
  ) {
    let date = 'Unknown';

    if (patient === undefined) return date;

    const { date_of_birth } = patient;

    if (date_of_birth !== undefined && date_of_birth !== 0) {
      let value = CalendarUtils.getUTCDay(date_of_birth);
      date = CalendarUtils.format(value, format);
      if (includeAge) date += ` (${this.getAge(patient)})`;
    }

    return date;
  }

  getOhipNumber(patient: Patient | undefined, placeholder?: string) {
    return this.getOhipNumberFromCard(patient?.health_card, undefined, placeholder);
  }

  getOhipNumberFromCard(
    healthCard: HealthCard | undefined,
    undefinedOnError = false,
    placeholder = 'N/A',
  ) {
    let ohip = placeholder;

    if (healthCard === undefined) {
      if (undefinedOnError) return undefined;
      return ohip;
    }

    let { health_number, version_code } = healthCard || {};

    if (health_number !== undefined) ohip = health_number;
    if (version_code !== undefined) ohip += version_code;

    return ohip;
  }

  getPhotoUrls(patient: Patient | undefined) {
    return [patient?.photo, this.getGender(patient) === 'Female' ? imageFemale : imageMale];
  }

  getAddress(patient: Patient | undefined) {
    let address = '';

    if (patient && patient.address) {
      let elements = [];
      if (patient.address.line1) elements.push(patient.address.line1);
      if (patient.address.line2) elements.push(patient.address.line2);
      if (patient.address.postcode) elements.push(patient.address.postcode);
      if (patient.address.city) elements.push(patient.address.city);
      if (patient.address.state) elements.push(patient.address.state);
      if (patient.address.country) elements.push(patient.address.country);

      address = elements.join(', ');
    }

    return address;
  }

  getContact(patient: Patient, includeSecondary = true) {
    let result = '';

    if (patient.phone) {
      let phone = parsePhoneNumberFromString(patient.phone);
      let phoneStr: string | undefined;
      if (phone) phoneStr = phone.formatNational();
      if (phoneStr) {
        result = phoneStr!;
      }
    }

    if (includeSecondary && patient.phone2) {
      let phone = parsePhoneNumberFromString(patient.phone2);
      let phoneStr: string | undefined;
      if (phone) phoneStr = phone.formatNational();
      if (result) result += ' / ';
      if (phoneStr) {
        result += phoneStr;
      }
    }

    if (!result) result = 'N/A';

    return result;
  }

  getGender(patient: Patient | undefined, short = false) {
    let result = 'Unknown Gender';
    if (patient === undefined) return result;
    let { gender } = patient;
    if (gender !== undefined) result = gender;
    if (short) result = result.charAt(0);
    return result;
  }

  /** @param {Appointment[]} appointments*/
  countUnreadAppointments(appointments: Appointment[]) {
    return appointments.reduce((acc, next) => {
      return acc + (next.read ? 0 : 1);
    }, 0);
  }

  countUnreadHealthRecords(healthRecords: PatientData | undefined) {
    if (!healthRecords) {
      return 0;
    }

    const EXCLUDE = [
      'dataSummary',
      'refills',
      'medicationReminderNotifications',
      'medicationReminders',
      'menstruation',
      'lmp',
      'administered_drugs',
      'unreadCount',
      'healthRecords',
      'faxes',
      'referrals',
      'referral_requests',
      'appointmentNoteDrafts',
      'communication_notes',
    ] as const;

    type Key = Exclude<keyof PatientData, (typeof EXCLUDE)[number]>;

    let count = 0;
    for (let index in healthRecords) {
      if (!(EXCLUDE as readonly string[]).includes(index))
        count += this.countUnread(healthRecords[index as Key]);
    }
    return count;
  }

  countUnread(data: { read?: boolean }[] = []) {
    if (!data.forEach) return 0;
    return data
      .map(({ read }) => (read ? 0 : 1))
      .reduce((acc: number, next: number) => acc + next, 0);
  }

  hasValidOhipCard(patient: Patient | undefined) {
    return patient?.health_card?.valid_card === true && !this.hasCardExpired(patient);
  }

  hasValidAddress(patient: Patient) {
    return patient.address !== undefined && !!patient.address.line1 && !!patient.address.postcode;
  }

  hasCardExpired(patient: Patient | undefined) {
    let expiryArr = patient?.health_card?.expiry_date || [];
    const expiry = expiryArr[0];
    if (expiry) return expiry < moment().unix();
    return false;
  }

  ohipCardNotice(patient: Patient | undefined) {
    if (patient?.health_card?.response_id === 'HAS_NOTICE') {
      return `${patient.health_card.response_description}. ${patient.health_card.response_action}`;
    }
  }

  showNotificationIfOhipInvalid(patient: Patient, showToastr = true) {
    let message;
    const isExpired = this.hasCardExpired(patient);
    const notice = this.ohipCardNotice(patient);
    if (isExpired) {
      let name = this.getName(patient, undefined, false, false);
      let date = CalendarUtils.format(patient.health_card?.expiry_date?.[0]!);
      message = `${name}'s OHIP card expired on ${date}`;
      if (showToastr) {
        MiscUtils.showToastrInfo({
          title: 'OHIP Expired',
          message,
        });
      }
    }
    if (!!notice && showToastr) {
      MiscUtils.showToastrInfo({
        title: 'OHIP Notice',
        message: notice,
      });
    }
    return [notice, message].filter(Boolean).join('. ');
  }
}

export default new PatientUtils();
